import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { SpinResponse } from '@cgs/data-access';
import {
  AbstractMouseEvent,
  Action,
  ActionActivator,
  Button,
  Container,
  EmptyAction,
  FunctionAction,
  InterpolateCopyAction,
  lerp,
  MouseDownEvent,
  ParallelSimpleAction,
  Rect,
  SceneObject,
  SequenceAction,
  SequenceSimpleAction,
  TextSceneObject,
  Vector2,
} from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { GameStateMachine } from '../../../../reels_engine/state_machine/game-state-machine';
import { JackPotWinPopupController } from '../controllers/jackpot_popup_controller';
import { BaseSlotPopupView, SlotPopups } from './base_popup_view';

export class JackPotPopupView extends BaseSlotPopupView<JackPotWinPopupController> {
  private _gameStateMachine: GameStateMachine<SpinResponse>;
  public get gameStateMachine(): GameStateMachine<SpinResponse> {
    return this._gameStateMachine;
  }
  private _textAnimDuration: number;
  private _incrementDuration: number;
  private _delayActivator: ActionActivator;
  private _popupAnimationAction: Action;
  private readonly numberFormatter: INumberFormatter;

  constructor(
    container: Container,
    root: SceneObject,
    popup: SceneObject,
    textAnimDuration: number,
    incrementDuration: number,
    closeWithButton: boolean
  ) {
    super(root, popup, null, SlotPopups.Jackpot);

    this._textAnimDuration = textAnimDuration;
    this._incrementDuration = incrementDuration;
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
    this._delayActivator = ActionActivator.withAction(this.view, new EmptyAction());

    if (closeWithButton) {
      const closeButtons = this.view.findAllById('closeBtn').map((t) => t as Button);
      for (const closeButton of closeButtons) {
        closeButton.clicked.listen(() => this._onBackToGameClicked());
      }
    } else {
      this._gameStateMachine = container.resolve<IGameStateMachineProvider>(
        GAME_STATE_MACHINE_PROVIDER_TOKEN
      ).gameStateMachine;
      this._gameStateMachine.init.entered.listen(() => {
        this.subscribeToSceneAnimationEvents();
        this.subscribeToTouchEvents();
      });
    }
  }

  private _onBackToGameClicked(): void {
    if (this._popupAnimationAction && !this._popupAnimationAction.isDone) {
      this._popupAnimationAction.end();
      this._delayActivator.action = new SequenceAction([
        new EmptyAction().withDuration(1.0),
        new FunctionAction(() => {
          this._delayActivator.stop();
          this.controller.onAnimCompleted();
        }),
      ]);
      this._delayActivator.start();
    } else {
      this.controller.onAnimCompleted();
    }
  }

  protected subscribeToSceneAnimationEvents() {
    this.view.stateMachine!.findById('anim')?.enterAction.done.listen(() => {
      this.controller.onAnimCompleted();
    });
  }

  private subscribeToTouchEvents() {
    this.view.touchable = true;
    this.view.touchArea = new Rect(new Vector2(-1000.0, -1000.0), new Vector2(1000.0, 1000.0));

    this.view.eventReceived.listen((e) => {
      if (e instanceof AbstractMouseEvent) {
        e.accept();
        if (e instanceof MouseDownEvent) {
          this.controller.onAnimCompleted();
        }
      }
    });
  }

  public postEvent(name: string) {
    this.view.stateMachine!.switchToState(name);
  }

  public setTotalWin(totalWin: number) {
    const texts = this.view.findAllById<TextSceneObject>('total_win_text');
    texts.forEach((e) => (e.text = this.numberFormatter.formatMoney(totalWin)));
  }

  public getAnimationAction(totalWin: number): Action {
    const incrementWinAction = new InterpolateCopyAction<number>().withInterpolateFunction(lerp);
    incrementWinAction
      .withDuration(this._incrementDuration)
      .withValues(1.0, totalWin)
      .valueChange.listen((win: number | undefined) => {
        this.setTotalWin(win as number);
      });

    const textAnimNode = this.view.findById('text');

    this._popupAnimationAction = new ParallelSimpleAction([
      new FunctionAction(() => {
        if (textAnimNode && textAnimNode.stateMachine) {
          textAnimNode.stateMachine.switchToState('default');
          textAnimNode.stateMachine.switchToState('textanim');
        }
      }),
      incrementWinAction,
      new SequenceSimpleAction([
        new EmptyAction().withDuration(this._textAnimDuration),
        new FunctionAction(() => {
          if (textAnimNode && textAnimNode.stateMachine) {
            textAnimNode.stateMachine.switchToState('endanim');
          }
        }),
      ]),
    ]);
    return this._popupAnimationAction;
  }
}
